import { FormField } from '@/components/shared/FormField';
import { ServiceUnavailableBanner } from '@/components/shared/ServiceUnavailableBanner';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useLoginSubmit } from '../hooks/use-login-submit';
import { PROVIDER_ID_MAX_LENGTH } from '../utils/provider-id';

/**
 * Provider access form (US-001, US-002). Its behaviour lives in {@link useLoginSubmit}.
 * While a maintenance window is announced the Login button is disabled (Proweb-38 follow-up) — the
 * maintenance banner above the card is what explains why.
 */
export function LoginForm({
  maintenanceActive = false,
}: {
  readonly maintenanceActive?: boolean;
}): React.JSX.Element {
  const { handleSubmit, errorMessage, serviceUnavailable, isPending } = useLoginSubmit();

  return (
    <form
      // Guarded here as well as on the button: the handler owns the invariant, so it holds for a
      // keyboard submit too, not only for the one route the disabled attribute happens to close.
      onSubmit={(event): void => {
        if (maintenanceActive) {
          event.preventDefault();
          return;
        }
        handleSubmit(event);
      }}
      aria-label="Provider login"
      className="flex flex-col gap-4"
    >
      {/* PP-028: the portal could not be reached, which is not a complaint about the Provider ID. */}
      {serviceUnavailable && <ServiceUnavailableBanner />}

      <FormField
        label="Provider ID"
        name="providerId"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="Enter Tax ID or NPI"
        maxLength={PROVIDER_ID_MAX_LENGTH}
        disabled={isPending}
        error={errorMessage}
        className="border-line focus:border-brand-600 h-12 bg-surface-muted text-[14.5px] focus:bg-surface"
      />

      <Button
        type="submit"
        size="lg"
        disabled={isPending || maintenanceActive}
        className="h-12 w-full"
      >
        {isPending && <Spinner />}
        {isPending ? 'Signing in…' : 'Login'}
      </Button>

      {isPending && (
        <p role="status" className="text-muted text-center text-sm">
          Verifying your Provider ID…
        </p>
      )}

      {/* The banner above the card also says this, but it is dismissible and the dismissal is
          remembered — this line is the disabled button's explanation that cannot be closed. */}
      {maintenanceActive && (
        <p role="status" className="text-destructive text-center text-sm">
          Login is temporarily unavailable during scheduled maintenance.
        </p>
      )}
    </form>
  );
}
